import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import BuscarDisponibilidad from "@/components/reservas/BuscarDisponibilidad";

// ─── helpers ────────────────────────────────────────────────────────────────
function fmtISODate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function fmtFecha(d) {
    const s = d.toLocaleDateString("es-AR", { weekday: "long", day: "numeric", month: "long" });
    return s.charAt(0).toUpperCase() + s.slice(1);
}
function hhmm(h, m) { return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`; }
function toMin(s) { const [h, m] = s.split(":"); return parseInt(h) * 60 + parseInt(m); }
function fromMin(min) { return hhmm(Math.floor(min / 60), min % 60); }
function rangoConsecutivo(labels) {
    if (labels.length < 2) return null;
    const sorted = [...labels].sort();
    for (let i = 1; i < sorted.length; i++) {
        if (toMin(sorted[i]) !== toMin(sorted[i - 1]) + 30) return null;
    }
    return `${sorted[0]} a ${fromMin(toMin(sorted[sorted.length - 1]) + 30)}`;
}

// ─── componente principal ───────────────────────────────────────────────────
export default function ReservarSala() {
    const [mode, setMode] = useState("buscar"); // buscar | calendario

    return (
        <div className="max-w-3xl pb-44 md:pb-40">
            <div className="flex items-end justify-between gap-3 mb-3 flex-wrap">
                <h1 className="text-2xl font-heading font-bold tracking-tight">Reservar sala</h1>
                <div className="inline-flex rounded-md border border-border overflow-hidden h-8">
                    <button
                        type="button"
                        onClick={() => setMode("buscar")}
                        data-testid="reservar-mode-buscar"
                        className={`px-3 text-xs font-medium transition-colors ${mode === "buscar" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-primary-soft"}`}
                    >
                        Buscar disponibilidad
                    </button>
                    <button
                        type="button"
                        onClick={() => setMode("calendario")}
                        data-testid="reservar-mode-calendario"
                        className={`px-3 text-xs font-medium transition-colors border-l border-border ${mode === "calendario" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-primary-soft"}`}
                    >
                        Calendario por sala
                    </button>
                </div>
            </div>

            {mode === "buscar" ? <BuscarDisponibilidad redirectOnSuccess="/inicio" /> : <CalendarioPorSala />}
        </div>
    );
}

// ─── modo CALENDARIO POR SALA (legado) ──────────────────────────────────────
function CalendarioPorSala() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const ue = user?.usuario_edificio;
    const myUeId = ue?.usuario_edif_id;

    const [salas, setSalas] = useState([]);
    const [salaId, setSalaId] = useState("");
    const [day, setDay] = useState(() => { const x = new Date(); x.setHours(0, 0, 0, 0); return x; });
    const [now, setNow] = useState(() => new Date());
    const [reservas, setReservas] = useState([]);
    const [selected, setSelected] = useState(() => new Set());
    const [submitting, setSubmitting] = useState(false);
    const [cancelingId, setCancelingId] = useState(null);

    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 60_000);
        return () => clearInterval(id);
    }, []);

    const today0 = useMemo(() => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; }, [now]); // eslint-disable-line
    const isToday = day.getTime() === today0.getTime();
    const isPastDay = day.getTime() < today0.getTime();
    const nowMin = now.getHours() * 60 + now.getMinutes();

    useEffect(() => { if (isPastDay) setDay(today0); }, [isPastDay, today0]);

    useEffect(() => {
        api.get("/salas").then((r) => {
            const activas = r.data.filter((s) => s.activa);
            setSalas(activas);
            if (activas[0]) setSalaId(activas[0].sala_id);
        });
    }, []);

    const fecha = fmtISODate(day);
    const dayIdx = (day.getDay() + 6) % 7;
    const sala = useMemo(() => salas.find((s) => s.sala_id === salaId), [salas, salaId]);
    const disponibleEseDia = sala?.dias_disponibles?.includes(dayIdx);

    const loadReservas = useCallback(() => {
        if (!salaId) return Promise.resolve();
        return api.get("/reservas", { params: { sala_id: salaId, fecha_desde: fecha, fecha_hasta: fecha } })
            .then((r) => setReservas(r.data))
            .catch(() => {});
    }, [salaId, fecha]);

    useEffect(() => { loadReservas(); }, [loadReservas]);
    useEffect(() => { setSelected(new Set()); }, [salaId, fecha]);

    useEffect(() => {
        if (!isToday) return;
        setSelected((prev) => {
            const next = new Set(prev);
            let changed = false;
            for (const label of prev) {
                if (toMin(label) < nowMin) { next.delete(label); changed = true; }
            }
            return changed ? next : prev;
        });
    }, [nowMin, isToday]);

    const slots = useMemo(() => {
        if (!sala) return [];
        const arr = [];
        const ini = toMin(sala.horario_inicio);
        const fin = toMin(sala.horario_fin);
        for (let m = ini; m < fin; m += 30) {
            arr.push({ h: Math.floor(m / 60), m: m % 60, label: hhmm(Math.floor(m / 60), m % 60) });
        }
        return arr;
    }, [sala]);

    const reservaDe = useCallback((slot) => {
        const ini = slot.h * 60 + slot.m;
        const fin = ini + 30;
        return reservas.find((r) => r.estado !== "cancelada"
            && toMin(r.hora_inicio) < fin && toMin(r.hora_fin) > ini);
    }, [reservas]);

    const cancelMine = async (r) => {
        if (cancelingId) return;
        setCancelingId(r.reserva_id);
        try { await api.post(`/reservas/${r.reserva_id}/cancelar`); } catch { }
        await loadReservas();
        setCancelingId(null);
    };

    const toggleSlot = (slot) => {
        if (isToday && (slot.h * 60 + slot.m) < nowMin) return;
        const r = reservaDe(slot);
        const mine = r && r.usuario_edif_id === myUeId;
        if (mine) { cancelMine(r); return; }
        if (r) return;
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(slot.label)) next.delete(slot.label); else next.add(slot.label);
            return next;
        });
    };

    const reservar = async () => {
        if (selected.size === 0 || submitting) return;
        setSubmitting(true);
        const labels = [...selected].sort();
        const results = await Promise.allSettled(
            labels.map((label) => api.post("/reservas", {
                sala_id: salaId,
                fecha,
                hora_inicio: label,
                hora_fin: fromMin(toMin(label) + 30),
                asistentes: 1,
                motivo: "",
            }))
        );
        const ok = results.filter((r) => r.status === "fulfilled").length;
        const fail = results.length - ok;
        if (fail === 0) {
            toast.success(ok === 1 ? "Reserva confirmada" : `${ok} bloques reservados`);
            setSelected(new Set());
            navigate("/inicio");
            return;
        }
        await loadReservas();
        setSelected(new Set());
        if (ok > 0) toast.warning("Algunos horarios ya no están disponibles.");
        else {
            const firstErr = results.find((r) => r.status === "rejected");
            toast.error(firstErr ? formatApiError(firstErr.reason) : "Algunos horarios ya no están disponibles.");
        }
        setSubmitting(false);
    };

    if (!ue) return <div className="text-muted-foreground">No tenés un perfil de edificio.</div>;
    const rango = rangoConsecutivo([...selected]);
    const count = selected.size;

    return (
        <div className="space-y-2">
            <Select value={salaId} onValueChange={setSalaId}>
                <SelectTrigger className="w-full" data-testid="reservar-sala-select"><SelectValue placeholder="Elegí una sala" /></SelectTrigger>
                <SelectContent>
                    {salas.map((s) => <SelectItem key={s.sala_id} value={s.sala_id}>{s.nombre}</SelectItem>)}
                </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={() => setDay(addDays(day, -1))} disabled={isToday} data-testid="day-prev"><ChevronLeft className="h-4 w-4" /></Button>
                <button type="button" onClick={() => setDay(today0)} className="flex-1 px-3 py-2 rounded-md border border-border bg-card hover:bg-primary-soft transition-colors text-sm font-heading font-bold text-center" data-testid="day-current">
                    {fmtFecha(day)}
                </button>
                <Button variant="outline" size="icon" onClick={() => setDay(addDays(day, 1))} data-testid="day-next"><ChevronRight className="h-4 w-4" /></Button>
            </div>

            <div className="flex items-center gap-x-3 gap-y-1 text-[10px] text-muted-foreground flex-wrap">
                <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-accent/40 ring-1 ring-accent/30" /> Disponible</span>
                <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-primary" /> Seleccionado</span>
                <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-accent ring-1 ring-primary" /> Mi reserva</span>
                <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40" /> Ocupado</span>
            </div>

            {sala && !disponibleEseDia && (
                <Card className="border-dashed"><CardContent className="p-8 text-center text-sm text-muted-foreground">La sala no está disponible este día.</CardContent></Card>
            )}

            {sala && disponibleEseDia && (
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                    {slots.map((s) => {
                        const r = reservaDe(s);
                        const mine = r && r.usuario_edif_id === myUeId;
                        const occupiedByOther = r && !mine;
                        const isSelected = selected.has(s.label);
                        const isCanceling = mine && cancelingId === r.reserva_id;
                        const isPastSlot = isToday && (s.h * 60 + s.m) < nowMin;

                        let cls;
                        if (isPastSlot && !mine) cls = "bg-muted/40 text-muted-foreground/50 border border-dashed border-border cursor-not-allowed line-through decoration-1";
                        else if (occupiedByOther) cls = "bg-muted/70 text-muted-foreground/60 border border-border cursor-not-allowed";
                        else if (mine) cls = "bg-accent text-accent-foreground border-2 border-primary shadow-sm hover:bg-accent/90 active:scale-95 transition-all";
                        else if (isSelected) cls = "bg-primary text-primary-foreground border-2 border-primary shadow-md scale-[0.98] hover:bg-primary/90 transition-all";
                        else cls = "bg-accent/15 text-accent border border-accent/30 hover:bg-accent/30 active:scale-95 transition-all";

                        let testid = `slot-${s.label}-free`;
                        if (isPastSlot && !mine) testid = `slot-${s.label}-past`;
                        else if (mine) testid = `slot-${s.label}-mine`;
                        else if (occupiedByOther) testid = `slot-${s.label}-other`;
                        else if (isSelected) testid = `slot-${s.label}-selected`;

                        return (
                            <button type="button" key={s.label} disabled={(!!occupiedByOther && !mine) || (isPastSlot && !mine) || isCanceling || submitting} onClick={() => toggleSlot(s)} data-testid={testid} className={`h-14 rounded-md font-mono text-sm font-semibold ${cls} ${isCanceling ? "opacity-60 pointer-events-none" : ""}`}>
                                {s.label}
                            </button>
                        );
                    })}
                </div>
            )}

            {count > 0 && (
                <div
                    className="fixed bottom-0 left-0 right-0 md:left-64 z-40 border-t border-border bg-background/95 backdrop-blur-md shadow-[0_-4px_16px_-8px_rgba(0,0,0,0.12)]"
                    style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 56px)" }}
                    data-testid="reservar-bar"
                >
                    <div className="max-w-2xl mx-auto px-4 pt-3 pb-2">
                        <div className="flex items-baseline justify-between gap-3 mb-2">
                            <p className="text-sm font-semibold leading-tight" data-testid="reservar-bar-count">{count} horario{count === 1 ? "" : "s"} seleccionado{count === 1 ? "" : "s"}</p>
                            <p className="text-xs text-muted-foreground font-mono truncate ml-2" data-testid="reservar-bar-range">{rango || [...selected].sort().join(" · ")}</p>
                        </div>
                        <Button onClick={reservar} disabled={submitting} className="w-full h-11" data-testid="reservar-confirmar">
                            {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Reservando…</> : "Reservar"}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
